/**
 * OCT28 WP-2b-2b-ii-b2-A (founder decision 2026-10-28, OPTION B) — the browser PRESENTS the database's end-of-day
 * judgement; it no longer derives a second projected finish from elapsed time minus worked time.
 *
 * THE authority (WP-2b-2b-ii-b2-A0): `ticket_eod_status(ticket)` — the alert engine's own judgement of the CURRENT
 * committed state (finish, the technician's dated day, valid overtime, holidays / absences, assignment, proposed
 * conversions, the reconcile lease / failed lanes, the clock). Only its `NONE` is a clearance, and only as of that read.
 * Nothing here recomputes it: no finish arithmetic, no boundary arithmetic, no "no row means clear".
 *
 * The reconcile freshness the Dashboard already polls (run_long_autobump_status, once a minute) is used ONLY to
 * withhold a clearance (a pending / stale / failing reconcile) and to say when the schedule was last reconciled —
 * never to grant one.
 */
import { useSyncExternalStore } from 'react';
import { supabase } from '@/integrations/supabase/client';

/** The reconcile freshness the Dashboard already computes (useRunLongReconcileOnOpen's view). */
export interface ScheduleFreshness {
  shopId: string;
  pending: boolean;
  stale: boolean;
  failing: boolean;
  lastReconciledAt: Date | null;
}

/** ticket_eod_status's answer (the fields the browser reads). */
export interface EodStatusRow {
  outcome: string;
  ticket_id?: string;
  technician_id?: string | null;
  work_date?: string | null;
  committed_finish?: string | null;
  boundary_at?: string | null;
  overrun_min?: number | null;
  conversion_id?: string | null;
  reason?: string | null;
  stored_conflict?: {
    conflict_id: string; version: number | null; overrun_min: number | null;
    committed_finish: string | null; boundary_at: string | null; conversion_id: string | null;
  } | null;
}

export type UnconfirmedReason =
  | 'reconciling' | 'unreconciled' | 'not_evaluated' | 'not_working_day' | 'stale' | 'failed' | 'unavailable';

export type ServerEodVerdict =
  | {
      state: 'conflict';
      overrunMin: number;
      committedFinish: string | null;
      boundaryAt: string | null;
      /** The decision's identity: the stored conflict + version when it agrees with this evaluation, else the evaluation itself. */
      decisionKey: string;
      /** The stored alert row agrees with the current evaluation (same finish and minutes). */
      storedAgrees: boolean;
      conversionId: string | null;
      lastReconciledAt: Date | null;
    }
  | { state: 'clear'; committedFinish: string | null; lastReconciledAt: Date | null }
  | { state: 'unconfirmed'; reason: UnconfirmedReason; lastReconciledAt: Date | null };

const sameInstant = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && Date.parse(a) === Date.parse(b);

/**
 * Pure. The verdict a person sees, from the database's answer (or its absence) and the reconcile freshness.
 *   CONFLICT          → conflict (the server's minutes and boundary; identity from the stored alert when it agrees)
 *   NONE              → clear — unless the reconcile is known pending / stale / failing (then withheld, never granted)
 *   anything else, an error, an empty or malformed answer → unconfirmed with its reason. Never clear.
 */
export function serverEodVerdict(input: { status: EodStatusRow | null | 'unavailable'; freshness: ScheduleFreshness | null }): ServerEodVerdict {
  const { status, freshness } = input;
  const last = freshness?.lastReconciledAt ?? null;
  const unconfirmed = (reason: UnconfirmedReason): ServerEodVerdict => ({ state: 'unconfirmed', reason, lastReconciledAt: last });
  if (!status || status === 'unavailable' || typeof status.outcome !== 'string') return unconfirmed('unavailable');
  switch (status.outcome) {
    case 'CONFLICT': {
      const s = status.stored_conflict ?? null;
      const minutes = Math.max(0, Number(status.overrun_min ?? 0) || 0);
      const storedAgrees = !!s && sameInstant(s.committed_finish, status.committed_finish) && Number(s.overrun_min ?? -1) === minutes;
      return {
        state: 'conflict', overrunMin: minutes, committedFinish: status.committed_finish ?? null,
        boundaryAt: status.boundary_at ?? null, storedAgrees, conversionId: status.conversion_id ?? null,
        decisionKey: storedAgrees && s
          ? `conflict:${s.conflict_id}:v${s.version ?? 0}`
          : `evaluation:${status.committed_finish ?? ''}:${minutes}:${status.boundary_at ?? ''}`,
        lastReconciledAt: last,
      };
    }
    case 'NONE':
      if (freshness?.pending) return unconfirmed('reconciling');
      if (freshness?.failing) return unconfirmed('failed');
      if (freshness?.stale) return unconfirmed('stale');
      return { state: 'clear', committedFinish: status.committed_finish ?? null, lastReconciledAt: last };
    case 'RECONCILING': return unconfirmed('reconciling');
    case 'UNRECONCILED': return unconfirmed('unreconciled');
    case 'NOT_EVALUATED': return unconfirmed(status.reason === 'not_a_working_day' ? 'not_working_day' : 'not_evaluated');
    case 'FAILED': return unconfirmed('failed');
    default: return unconfirmed('unavailable');   // NOT_FOUND, or an outcome this browser does not know
  }
}

/** The words a person sees for an unconfirmed check (Option (a): never "fine", always when it was last confirmed). */
export function describeUnconfirmed(v: Extract<ServerEodVerdict, { state: 'unconfirmed' }>, formatTime: (d: Date) => string): string {
  const since = v.lastReconciledAt ? ` Schedule last reconciled ${formatTime(v.lastReconciledAt)}.` : '';
  const why: Record<UnconfirmedReason, string> = {
    reconciling: 'The schedule is being updated.',
    unreconciled: 'The schedule has not caught up yet.',
    not_evaluated: 'This job is not covered by the end-of-day check.',
    not_working_day: 'There is no working day scheduled for this job.',
    stale: 'The schedule has not been updated recently.',
    failed: 'The schedule could not be checked; the server is retrying.',
    unavailable: 'The schedule could not be read.',
  };
  return `End-of-day check not confirmed. ${why[v.reason]}${since}`;
}

/** The words for a CONFIRMED conflict (it stays on the card after the modal is dismissed, and when no modal applies). */
export function describeEodConflict(overrunMin: number, presentation: 'modal' | 'proposal_pending' | 'chain_unrepresentable' | null): string {
  const head = `End-of-day overrun: ${overrunMin} min past the authorized end of day.`;
  if (presentation === 'proposal_pending') return `${head} A rollover proposal is waiting for a decision.`;
  if (presentation === 'chain_unrepresentable') {
    return `${head} This multi-day repair needs a decision from a service advisor or manager (not available in the end-of-day dialog).`;
  }
  return `${head} A service advisor or manager needs to decide.`;
}

/**
 * Founder decision (Option a): what a Resume does with the server's answer. Recording work is never blocked by a
 * scheduling outage; it authorizes nothing (no overtime, no rollover, no reservation). A CONFIRMED conflict keeps the
 * existing human decision.
 */
export function resumeEodAction(v: ServerEodVerdict): 'proceed' | 'proceed_unconfirmed' | 'decision_required' {
  if (v.state === 'conflict') return 'decision_required';
  return v.state === 'clear' ? 'proceed' : 'proceed_unconfirmed';
}

// ── shared state: the freshness (published by the Dashboard; no second poll), per-tab verification, shown decisions ──
let freshness: ScheduleFreshness | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export function publishScheduleFreshness(next: ScheduleFreshness | null): void {
  const same = freshness && next && freshness.shopId === next.shopId && freshness.pending === next.pending
    && freshness.stale === next.stale && freshness.failing === next.failing
    && (freshness.lastReconciledAt?.getTime() ?? null) === (next.lastReconciledAt?.getTime() ?? null);
  if (same || (!freshness && !next)) return;
  freshness = next;
  emit();
}
/** The reconcile is fresh: known, not pending, not stale, fully successful. */
export function isScheduleFresh(f: ScheduleFreshness | null): boolean {
  return !!f && !f.pending && !f.stale && !f.failing && !!f.lastReconciledAt;
}
export function getScheduleFreshness(shopId: string | null | undefined): ScheduleFreshness | null {
  return freshness && shopId && freshness.shopId === shopId ? freshness : null;
}
export function useScheduleFreshness(shopId: string | null | undefined): ScheduleFreshness | null {
  return useSyncExternalStore(subscribe, () => getScheduleFreshness(shopId), () => null);
}

/**
 * A Resume that went ahead on an unconfirmed check keeps the ticket "awaiting verification" in THIS tab (sessionStorage:
 * it survives a reload of the tab). It is display state only — not server authority and not shared across devices
 * (other devices see the server's own alert). Keyed by shop + ticket, it remembers the technician it was recorded for
 * and is dropped on a verified outcome, a terminal ticket, or a reassignment.
 */
const VERIFY_KEY = 'optibay.eodPendingVerification.v2';
type VerifyEntry = { since: string; techId: string | null };
const vkey = (shopId: string, ticketId: string) => `${shopId}:${ticketId}`;
function readVerify(): Record<string, VerifyEntry> {
  try {
    const v = JSON.parse(sessionStorage.getItem(VERIFY_KEY) ?? '{}');
    return v && typeof v === 'object' ? (v as Record<string, VerifyEntry>) : {};
  } catch { return {}; }
}
function writeVerify(v: Record<string, VerifyEntry>) {
  try { sessionStorage.setItem(VERIFY_KEY, JSON.stringify(v)); } catch { /* storage unavailable: in-memory only */ }
}
let verifyMemo: Record<string, VerifyEntry> = readVerify();
export function markEodPendingVerification(shopId: string, ticketId: string, techId: string | null, atIso: string): void {
  verifyMemo = { ...verifyMemo, [vkey(shopId, ticketId)]: { since: atIso, techId } }; writeVerify(verifyMemo); emit();
}
export function clearEodPendingVerification(shopId: string, ticketId: string): void {
  const k = vkey(shopId, ticketId);
  if (!(k in verifyMemo)) return;
  const { [k]: _drop, ...rest } = verifyMemo; verifyMemo = rest; writeVerify(verifyMemo); emit();
}
export function getEodPendingVerification(shopId: string | null | undefined, ticketId: string): VerifyEntry | null {
  return shopId ? verifyMemo[vkey(shopId, ticketId)] ?? null : null;
}
export function useEodPendingVerification(shopId: string | null | undefined, ticketId: string): VerifyEntry | null {
  return useSyncExternalStore(subscribe, () => getEodPendingVerification(shopId, ticketId), () => null);
}

/**
 * Decisions raised in this tab, by identity (the stored conflict + its version, or the evaluation itself). The same
 * unchanged decision is not reopened on every re-read; a changed conflict (new version, minutes, finish or boundary)
 * is a new decision. A dismissed decision is not re-raised by polling, but stays visible on the card.
 */
const shownDecisions = new Set<string>();
export function claimEodDecisionShown(shopId: string, ticketId: string, decisionKey: string): boolean {
  const k = `${shopId}:${ticketId}:${decisionKey}`;
  if (shownDecisions.has(k)) return false;
  shownDecisions.add(k);
  return true;
}

/** Test seam: reset the module state (and re-read the tab storage, as a reload would). */
export function __resetServerEodStateForTests(opts: { keepStorage?: boolean } = {}): void {
  freshness = null; shownDecisions.clear();
  if (!opts.keepStorage) writeVerify({});
  verifyMemo = readVerify();
}

// ── the read (one RPC; RLS / membership enforced in the database) ───────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Db = any;

/** Ask the database for the ticket's current end-of-day judgement. An error or a malformed answer is "unavailable". */
export async function loadServerEodVerdict(ticketId: string, shopId: string, db: Db = supabase): Promise<{ verdict: ServerEodVerdict; status: EodStatusRow | null }> {
  let status: EodStatusRow | null = null;
  try {
    const { data, error } = await db.rpc('ticket_eod_status', { p_ticket: ticketId });
    if (!error && data && typeof data === 'object' && typeof (data as EodStatusRow).outcome === 'string'
        && (data as EodStatusRow).ticket_id === ticketId) {
      status = data as EodStatusRow;
    }
  } catch { status = null; }
  return { verdict: serverEodVerdict({ status: status ?? 'unavailable', freshness: getScheduleFreshness(shopId) }), status };
}
